import React, {useEffect} from "react";
import {DOWNLOAD_UNIBUDDY_APP_BANNER} from "shared/constants";
import {useBooleanFeatureFlag} from "ub-feature-flagging-react";
import {Box, Text} from "@unibuddy/patron";
import {IconChevronRight} from "@unibuddy/icons";
import {Trans, useTranslation} from "@unibuddy/intl";
import {useHistory, useLocation} from "react-router-dom";
import {rootURL} from "shared/Utils/WhereAmI";
import useAnalytics from "shared/AnalyticsNew/hooks/useAnalytics";
import {sharedEvents} from "shared/AnalyticsNew/constants/productSpecification";

import {useIsScreenSizeLargerThan} from "../../../hooks/useIsScreenSizeLargerThan";
import {useUniversity} from "../UniversityProvider/UniversityProvider";
import {INTL_DOWNLOAD_APP_BANNER, INTL_UNIBUDDY_APP_LOGO} from "../../../translations/common";

// In-memory event tracking for the download app banner
const pagesTrackEventSentOn = new Set<string>();

type DownloadUnibuddyAppBannerProps = {
    page: string;
};

export const DownloadUnibuddyAppBanner = ({page}: DownloadUnibuddyAppBannerProps) => {
    const {t} = useTranslation();
    const downloadUnibuddyAppBannerEnabled = useBooleanFeatureFlag(
        DOWNLOAD_UNIBUDDY_APP_BANNER.key,
        DOWNLOAD_UNIBUDDY_APP_BANNER.value,
    );

    const isDesktop = useIsScreenSizeLargerThan(632);
    const history = useHistory();
    const location = useLocation();
    const {trackEvent} = useAnalytics();
    const {slug: universitySlug} = useUniversity();

    useEffect(() => {
        const isPageAlreadyTracked = pagesTrackEventSentOn.has(page);
        if (isPageAlreadyTracked) return;

        if (downloadUnibuddyAppBannerEnabled) {
            trackEvent(sharedEvents.downloadAppBannerDisplayed.name, {
                universitySlug,
                page,
                bannerName: "Download Unibuddy App Prompt",
            });
            pagesTrackEventSentOn.add(page);
        }
    }, [downloadUnibuddyAppBannerEnabled, trackEvent, universitySlug, page]);

    if (!downloadUnibuddyAppBannerEnabled) return null;

    return (
        <Box
            display="flex"
            justifyContent="space-between"
            borderColor="grey150"
            borderWidth={1}
            bgColor="grey50"
            borderRadius="xsmall"
            padding="small"
            width="100%"
            marginBottom="medium"
            as="button"
            onClick={() => {
                trackEvent(sharedEvents.downloadAppBannerClicked.name, {
                    universitySlug,
                    page,
                    cta: "Download App",
                });
                history.push(`${rootURL(location.pathname)}/inbox/app/download`);
            }}
        >
            <Box display="flex" alignItems="center" gap="small-medium">
                <Box flexShrink={0} alignItems="center" maxH="48px">
                    <img
                        src="https://s3.eu-west-2.amazonaws.com/unibuddy-static/icons/app-icon.png"
                        alt={t(INTL_UNIBUDDY_APP_LOGO.key, INTL_UNIBUDDY_APP_LOGO.defaultValue)}
                        width="48px"
                        height="48px"
                    />
                </Box>

                <Text size="large" weight="400" color="grey900">
                    <Trans t={t} i18nKey={INTL_DOWNLOAD_APP_BANNER.key}>
                        {"Download the "}
                        <span style={{fontWeight: "bold"}}>{"Unibuddy App!"}</span>
                    </Trans>
                </Text>
            </Box>

            <Box display="flex" alignItems="center" gap="small" flexShrink={0}>
                {isDesktop && (
                    <img
                        src="https://s3.eu-west-2.amazonaws.com/unibuddy-static/icons/app-store.jpg"
                        alt=""
                        width="150px"
                        height="46px"
                    />
                )}
                {isDesktop && (
                    <img
                        src="https://s3.eu-west-2.amazonaws.com/unibuddy-static/icons/google-play.png"
                        alt=""
                        width="150px"
                        height="46px"
                    />
                )}

                <IconChevronRight color="grey900" />
            </Box>
        </Box>
    );
};
